import { auth } from '@/app/config/firebase'

import { getHttpStatusName } from './httpStatus'

interface RequestOptions extends RequestInit {
  skipAuth?: boolean
}

class ApiError extends Error {
  status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

class UploadAbortError extends Error {
  constructor() {
    super('Upload aborted by user')
    this.name = 'UploadAbortError'
  }
}

class HttpClient {
  private async getAuthToken(): Promise<string | null> {
    const { currentUser } = auth
    if (!currentUser) {
      return null
    }

    try {
      // Get the Firebase ID token
      const token = await currentUser.getIdToken()
      return token
    } catch (error) {
      console.error('Failed to get Firebase ID token:', error)
      return null
    }
  }

  private async prepareHeaders(options?: RequestOptions): Promise<HeadersInit> {
    const headers: Record<string, string> = {}

    // Only add Content-Type for JSON requests (not for FormData)
    if (options?.headers && !(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json'
    }

    // Add authentication token if not skipped
    if (!options?.skipAuth) {
      const token = await this.getAuthToken()
      if (token) {
        headers['x-id-token'] = token
        headers['authorization'] = `Bearer ${token}`
      }
    }

    return { ...headers, ...options?.headers }
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const statusName = getHttpStatusName(response.status)
      let errorMessage = `${statusName}. Bitte versuchen Sie es erneut.`
      try {
        const responseBody = await response.json()
        errorMessage = responseBody.message || responseBody.error || errorMessage
      } catch {
        try {
          const textBody = await response.text()
          errorMessage = textBody || errorMessage
        } catch {
          // Use default error message
        }
      }
      throw new ApiError(errorMessage, response.status)
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return null as T
    }

    // Verify JSON response for non-empty responses
    const contentType = response.headers.get('content-type')
    if (contentType && contentType.includes('application/json')) {
      return response.json() as Promise<T>
    }

    // Return text for non-JSON responses
    return response.text() as Promise<T>
  }

  async fetch(url: string, options?: RequestOptions): Promise<Response> {
    const headers = await this.prepareHeaders(options)

    return fetch(url, {
      ...options,
      headers,
    })
  }

  async get<T>(url: string, options?: RequestOptions): Promise<T> {
    const response = await this.fetch(url, {
      ...options,
      method: 'GET',
    })
    return this.handleResponse<T>(response)
  }

  async post<T>(url: string, body?: unknown, options?: RequestOptions): Promise<T> {
    let requestBody: BodyInit | undefined
    if (body instanceof FormData) {
      requestBody = body
    } else if (body) {
      requestBody = JSON.stringify(body)
    }

    const response = await this.fetch(url, {
      ...options,
      method: 'POST',
      body: requestBody,
    })
    return this.handleResponse<T>(response)
  }

  async put<T>(url: string, body?: unknown, options?: RequestOptions): Promise<T> {
    let requestBody: BodyInit | undefined
    if (body instanceof FormData) {
      requestBody = body
    } else if (body) {
      requestBody = JSON.stringify(body)
    }

    const response = await this.fetch(url, {
      ...options,
      method: 'PUT',
      body: requestBody,
    })
    return this.handleResponse<T>(response)
  }

  async delete<T>(url: string, options?: RequestOptions): Promise<T | null> {
    const response = await this.fetch(url, {
      ...options,
      method: 'DELETE',
    })
    return this.handleResponse<T>(response)
  }

  /**
   * Upload a file using multipart/form-data
   * @param url - The URL to upload to
   * @param file - The file to upload
   * @param additionalData - Optional additional form fields
   * @param onProgress - Optional progress callback (not implemented in fetch API)
   */
  async uploadFile<T>(
    url: string,
    file: File,
    additionalData?: Record<string, string | number>,
    onProgress?: (progress: number) => void
  ): Promise<T> {
    const formData = new FormData()
    formData.append('file', file)

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, String(value))
      })
    }

    // Note: Progress tracking with fetch API requires using XMLHttpRequest instead
    // This is a simplified version without progress tracking
    if (onProgress) {
      console.warn('Progress tracking is not supported with native fetch API')
    }

    const response = await this.fetch(url, {
      method: 'POST',
      body: formData,
    })

    return this.handleResponse<T>(response)
  }

  /**
   * Upload a file with progress tracking using XMLHttpRequest
   * @param url - The URL to upload to
   * @param file - The file to upload
   * @param additionalData - Optional additional form fields
   * @param onProgress - Progress callback (0-100)
   * @param options - Optional request options
   * @returns Object containing the upload promise and abort function
   */
  uploadFileWithProgress<T>(
    url: string,
    file: File,
    additionalData?: Record<string, string | number>,
    onProgress?: (progress: number) => void,
    options?: RequestOptions
  ): { promise: Promise<T>; abort: () => void } {
    const xhr = new XMLHttpRequest()

    const promise = (async () => {
      // Get authentication token
      const token = options?.skipAuth ? null : await this.getAuthToken()

      return new Promise<T>((resolve, reject) => {
        // Track upload progress
        if (onProgress) {
          xhr.upload.addEventListener('progress', event => {
            if (event.lengthComputable) {
              const percentComplete = Math.round((event.loaded / event.total) * 100)
              onProgress(percentComplete)
            }
          })
        }

        // Handle completion
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              // Handle 204 No Content
              if (xhr.status === 204) {
                resolve(null as T)
                return
              }

              const contentType = xhr.getResponseHeader('content-type')
              if (contentType && contentType.includes('application/json')) {
                const response = JSON.parse(xhr.responseText) as T
                resolve(response)
              } else {
                resolve(xhr.responseText as T)
              }
            } catch {
              reject(
                new Error(
                  'Die Serverantwort konnte nicht verarbeitet werden. Bitte versuchen Sie es erneut.'
                )
              )
            }
          } else {
            const statusName = getHttpStatusName(xhr.status)
            let errorMessage = `${statusName}. Bitte versuchen Sie es erneut.`
            try {
              const errorBody = JSON.parse(xhr.responseText)
              errorMessage = errorBody.message || errorBody.error || errorMessage
            } catch {
              errorMessage = xhr.responseText || errorMessage
            }
            reject(new ApiError(errorMessage, xhr.status))
          }
        })

        // Handle errors
        xhr.addEventListener('error', () => {
          reject(
            new ApiError(
              'Netzwerkfehler beim Hochladen. Bitte überprüfen Sie Ihre Internetverbindung.',
              0
            )
          )
        })

        xhr.addEventListener('abort', () => {
          reject(new UploadAbortError())
        })

        // Prepare the request
        xhr.open('POST', url)

        // Add authentication header
        if (token) {
          xhr.setRequestHeader('x-id-token', token)
        }

        // Add custom headers
        if (options?.headers) {
          Object.entries(options.headers).forEach(([key, value]) => {
            if (typeof value === 'string') {
              xhr.setRequestHeader(key, value)
            }
          })
        }

        // Prepare form data
        const formData = new FormData()
        formData.append('file', file)

        if (additionalData) {
          Object.entries(additionalData).forEach(([key, value]) => {
            formData.append(key, String(value))
          })
        }

        // Send the file
        xhr.send(formData)
      })
    })()

    return {
      promise,
      abort: () => xhr.abort(),
    }
  }
}

export const httpClient = new HttpClient()
export { ApiError, UploadAbortError }
export type { RequestOptions }
